import imageCompression from 'browser-image-compression';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useIsMounted } from '../../components/React';
import {
  arrayOrStringToString,
  config,
  displayJson,
  formatString,
  logError,
  piidIsHome,
  stringUpperCaseFirstLetter,
  registerLoader,
  completeLoader,
  suspendLoader,
  resumeLoader,
} from '../../utils';
import useAuth from '../auth/useAuth';

import initialStringsEn from './initialStringsEn.json';
import initialStringsFr from './initialStringsFr.json';
import TownAssetsContext from './TownAssetsContext';
import checkStaffMissionCatalogDrift from './utils/checkStaffMissionCatalogDrift';
import flattenQrMenu from './utils/flattenQrMenu';

// Same bundled strings as InitialAssetsProvider. They back translate() until
// the server language loads, so surfaces rendered under this provider while
// logged out (the login page, SupportLink) still resolve keys.
const BUNDLED_STRINGS = {
  fr: initialStringsFr,
  en: initialStringsEn,
};
const bundledLangCode = (globalThis.navigator?.language ?? 'fr').slice(0, 2).toLowerCase();
const bundledLanguage = BUNDLED_STRINGS[bundledLangCode] || BUNDLED_STRINGS.fr;

// Once a translate() miss has confirmed the language rev, later misses report without asking again
const LANGUAGE_RECHECK_MIN_MS = 60000;

// Register granular loaders at module load time (before React renders)
const loaderIds = {
  languages: registerLoader('TownAssetsProvider.languages'),
  language: registerLoader('TownAssetsProvider.language'),
  ccatCodes: registerLoader('TownAssetsProvider.ccatCodes'),
};

const maxCacheItems = 70;
// Two servers can hand out the same path for different images, so the partner a
// blob was fetched from belongs in the key
const securedImgKey = (urlPath, piid) => ((piidIsHome(piid)) ? urlPath : `${piid}:${urlPath}`);
// Over HTTP/2 the browser no longer holds requests back per origin, so a page full
// of images puts them all on the wire at once and trips the gateway flood ceiling
const maxSecuredImgInFlight = 6;

async function compressImageIfNeeded(file, maxSizeMB = 0.9) {
  const fileSizeMB = file.size / 1024 / 1024;

  if (fileSizeMB <= maxSizeMB) {
    return file;
  }

  const options = {
    maxSizeMB,
    // maxWidthOrHeight: 1920,
    useWebWorker: true,
  };

  try {
    const compressedFile = await imageCompression(file, options);

    return compressedFile;
  } catch (error) {
    logError(
      'Error while compressing the file:',
      'TownAssetsProvider.compressGroupImage.error',
      {
        err: error?.message,
        fileSizeMB,
        fileType: file?.type,
      },
    );
    throw error;
  }
}

async function compressCommImage(file) {
  if (file.type === 'image/svg+xml') {
    throw new Error('SVG images are not supported for comm items');
  }
  try {
    const compressedFile = await imageCompression(
      file,
      {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      },
    );
    return compressedFile;
  } catch (error) {
    logError(
      `Error while compressing comm image: ${error}`,
      'TownAssetsProvider.compressCommImage.error',
      {
        err: error?.message,
        fileSizeMB: file?.size && (file.size / 1024 / 1024),
        fileType: file?.type,
      },
    );
    throw error;
  }
}

const flattenMenu = (
  subMenu,
  depth,
  newFlatMenu,
  domain,
  key,
  parentLvl,
  parentGroupCat,
  parentOptionalGroupCat,
  parentPath,
) => {
  if ((!subMenu) || (!subMenu.length)) return;

  let prevDomain;

  subMenu.forEach((menuItem, index) => {
    const thisKey = (depth)
      ? `${key}_${index}`
      : `${index}`;
    const thisDomain = (depth)
      ? domain
      : menuItem.domain;
    const { groupCat, optionalGroupCat, content } = menuItem;
    const thisLvl = (menuItem.level === undefined)
      ? parentLvl
      : menuItem.level;
    const thisGroupCat = (groupCat === undefined)
      ? parentGroupCat
      : groupCat;
    const thisOptionalGroupCat = (optionalGroupCat === undefined)
      ? parentOptionalGroupCat
      : optionalGroupCat;

    let path;
    if (!menuItem.code) path = thisDomain;
    else if (parentPath) path = `${parentPath}/${menuItem.code}`;
    else path = `/${menuItem.code}`;

    if ((!depth) && (thisDomain !== prevDomain)) {
      newFlatMenu.push({
        ccatDomain: thisDomain,
        key: `domain_${thisDomain}`,
        lvl: 0,
        path,
      });
    }

    prevDomain = thisDomain;

    newFlatMenu.push({
      ccatDomnCode: `${thisDomain}.${menuItem.code}`,
      ccatDomain: thisDomain,
      ccatCode: menuItem.code,
      depth,
      key: thisKey,
      lvl: thisLvl,
      end: !(content && (content.length)),
      groupCat: thisGroupCat,
      optionalGroupCat: thisOptionalGroupCat,
      path,
    });

    flattenMenu(
      content,
      depth + 1,
      newFlatMenu,
      thisDomain,
      thisKey,
      thisLvl,
      thisGroupCat,
      thisOptionalGroupCat,
      path,
    );
  });
};

function TownAssetsProvider(props) {
  const {
    children,
    defaultLanguage = 'fr',
    public: isPublic,
  } = props;
  const { auth } = useAuth();
  const {
    superagentGetAsync,
    superagentPostAsync,
    info: authInfo,
    authStatus,
    cstmrGet,
    cstmrPost,
    cstmrPostBlob,
  } = auth;

  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState({ rev: null });
  const [flatCcatMenu, setFlatCcatMenu] = useState([]);
  const [languagesPending, setLanguagesPending] = useState(false);
  const [languagePending, setLanguagePending] = useState(false);
  const flatCcatMenuPending = useRef(false);
  const [qrMenus, setQrMenus] = useState([]);
  const [flatQrMenus, setFlatQrMenus] = useState([]);
  const isMountedRef = useIsMounted();
  const [imagesList, setImagesList] = useState();
  const securedImgCache = useRef(new Map());
  // What every asker for one image waits on, so a page showing it ten times asks once
  const securedImgPending = useRef(new Map());
  const securedImgQueue = useRef([]);
  const securedImgRunning = useRef(0);
  const languageRef = useRef(language);
  // One server check serves every translate() miss raised while it runs
  const languageRecheck = useRef({
    lastAt: 0,
    inFlight: false,
    missing: [],
  });

  useEffect(
    () => {
      languageRef.current = language;
    },
    [language],
  );

  const ready = useMemo(
    () => (
      !!((language?.language === languagePending)
        && (flatCcatMenu.length))
    ),
    [language, languagePending, flatCcatMenu.length],
  );

  const staffMissionCatalogDrift = useMemo(
    () => ((flatQrMenus.length)
      ? checkStaffMissionCatalogDrift(flatQrMenus)
      : null),
    [flatQrMenus],
  );

  useEffect(
    () => {
      if ((!staffMissionCatalogDrift) || config.dev) return;

      logError(
        'TownAssetsProvider - staff mission catalog drift vs QR menu',
        'TownAssetsProvider.staffMissionCatalog.drift',
        staffMissionCatalogDrift,
      );
    },
    [staffMissionCatalogDrift],
  );

  if (config.dev && staffMissionCatalogDrift) {
    const { missingFromCatalog, missingFromQrMenu } = staffMissionCatalogDrift;
    const lines = ['Staff mission catalog drift vs QR menu:'];
    if (missingFromCatalog.length) {
      lines.push(`\nIn QR menu but missing from staffMissionCatalog.json (add them):\n  - ${
        missingFromCatalog.join('\n  - ')}`);
    }
    if (missingFromQrMenu.length) {
      lines.push(`\nIn staffMissionCatalog.json but not in QR menu (remove them):\n  - ${
        missingFromQrMenu.join('\n  - ')}`);
    }
    throw new Error(lines.join('\n'));
  }

  // Suspend/resume all loaders based on auth status
  // Assets cannot be loaded when logged out
  useEffect(
    () => {
      if (authStatus === 'loggedOut') {
        suspendLoader(loaderIds.languages);
        suspendLoader(loaderIds.language);
        suspendLoader(loaderIds.ccatCodes);
      } else if (authStatus === 'loggedIn') {
        resumeLoader(loaderIds.languages);
        resumeLoader(loaderIds.language);
        resumeLoader(loaderIds.ccatCodes);
      } else if (isPublic && (authStatus !== 'init')) {
        // Public pages: allow language loading once auth resolves (even if not logged in)
        resumeLoader(loaderIds.languages);
        resumeLoader(loaderIds.language);
        suspendLoader(loaderIds.ccatCodes);
      }
      // authStatus === 'init': loaders stay pending (waiting)
    },
    [authStatus, isPublic],
  );

  // Complete individual loaders as each resource becomes available
  // Note: authStatus dependency ensures re-evaluation after login (data may already be cached)
  useEffect(
    () => {
      const authReady = (authStatus === 'loggedIn')
                      || (isPublic && (authStatus !== 'init'));
      if (authReady && (languages.length > 0)) {
        completeLoader(loaderIds.languages);
      }
    },
    [languages, authStatus, isPublic],
  );

  useEffect(
    () => {
      const authReady = (authStatus === 'loggedIn') || (isPublic && (authStatus !== 'init'));
      if (authReady && language?.rev) {
        completeLoader(loaderIds.language);
      }
    },
    [language, authStatus, isPublic],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn') && (flatCcatMenu.length > 0)) {
        completeLoader(loaderIds.ccatCodes);
      }
    },
    [flatCcatMenu, authStatus],
  );

  const logMissingTranslation = useCallback(
    (reason, type, code) => {
      logError(
        `TownAssetsProvider - Missing language type="${
          type}" code="${code}" language.rev="${languageRef.current.rev}"`,
        `TownAssetsProvider.translate.${reason}`,
        {
          type,
          code,
          languageRev: languageRef.current.rev,
        },
      );
    },
    [],
  );

  //
  // Fetch the server language again and swap it in when its rev moved on. A window kept open
  // across a deploy still holds the language it loaded at the time, and nothing else refreshes it.
  //
  const refetchLanguageIfOutdated = useCallback(
    async () => {
      const current = languageRef.current;
      try {
        const url = `${config.town.back.urlPrefix
                    + config.town.back.language.get}/${current.language}`;
        const res = await superagentGetAsync(url);
        const fresh = res.body?.rslt?.data?.[0];
        if ((res.status !== 200) || (!fresh?.rev) || (fresh.rev === current.rev)) return false;

        console.log(`refetchLanguageIfOutdated rev ${current.rev} -> ${fresh.rev}`);
        setLanguage(fresh);
        return true;
      } catch {
        return false;
      }
    },
    [superagentGetAsync],
  );

  //
  // A missing key is first taken as a stale language: misses are held while the server rev is
  // checked, and only reported when that rev is the one already loaded.
  //
  const reportMissingTranslation = useCallback(
    (reason, type, code) => {
      const recheck = languageRecheck.current;
      if (recheck.inFlight) {
        recheck.missing.push({ reason, type, code });
        return;
      }

      const recentlyChecked = (Date.now() - recheck.lastAt) < LANGUAGE_RECHECK_MIN_MS;
      if (recentlyChecked || (!languageRef.current.language)) {
        logMissingTranslation(reason, type, code);
        return;
      }

      recheck.inFlight = true;
      recheck.missing = [{ reason, type, code }];
      // Use void to make it clear we do not want to wait this async function:
      void refetchLanguageIfOutdated().then((outdated) => {
        recheck.lastAt = Date.now();
        recheck.inFlight = false;
        if (!outdated) {
          recheck.missing.forEach((miss) => logMissingTranslation(miss.reason, miss.type, miss.code));
        }
        recheck.missing = [];
      });
    },
    [logMissingTranslation, refetchLanguageIfOutdated],
  );

  const translate = useCallback(
    (
      code,
      type = 'generic',
      params = undefined,
      capitalize = true,
      ignoreMissing = false,
      debug = false,
    ) => {
      if (!code) return '';

      const buildTranslation = (raw) => {
        let ret = arrayOrStringToString(raw);
        if (params) ret = formatString(ret, params);
        if (capitalize) ret = stringUpperCaseFirstLetter(ret);
        return ret;
      };

      // Use only server language once it is loaded.
      const source = language.data ? language : bundledLanguage;

      if (source.data?.[type]) {
        const val = source.data[type][code];
        if (val) return buildTranslation(val);
        if (val === null) return null;

        if (!ignoreMissing) reportMissingTranslation('missingCode', type, code);

        // eslint-disable-next-line no-debugger
        if (language.rev && debug) debugger;
      } else if (!ignoreMissing) {
        reportMissingTranslation('missingType', type, code);
      }

      if (ignoreMissing) return null;
      return (`--${code}--`);
    },
    [language, reportMissingTranslation],
  );

  const loadLanguages = useCallback(
    async () => {
      let error;
      let res;

      // console.log('loadLanguage');
      setLanguagesPending(true);
      try {
        const url = config.town.back.urlPrefix
                  + config.town.back.language.list;

        // console.log(`withTownAssets get language at ${url}`);
        res = await superagentGetAsync(url);

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        setLanguages(res.body.rslt.data);
        return;
      }
      logError(
        `loadLanguages Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'TownAssetsProvider.loadLanguages.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
    },
    [superagentGetAsync],
  );

  const loadLanguage = useCallback(
    async (langCode = 'fr') => {
      let error;
      let res;
      if (langCode === languagePending) return; // NOSONAR

      setLanguagePending(langCode);
      try {
        const url = `${config.town.back.urlPrefix
                    + config.town.back.language.get}/${langCode}`;

        res = await superagentGetAsync(url);

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        console.log(`loadLanguage ${res.body.rslt.data[0]?.language} rev ${res.body.rslt.data[0]?.rev}`);
        setLanguage(res.body.rslt.data[0]);
        return;
      }
      logError(
        `loadLanguage Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'TownAssetsProvider.loadLanguage.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
    },
    [languagePending, superagentGetAsync],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn')
            && authInfo
            && language
            && (language.language === languagePending)
            && (!flatCcatMenu.length)
            && (!flatCcatMenuPending.current)
      ) {
        flatCcatMenuPending.current = true;

        const url = config.town.back.urlPrefix
                  + config.town.back.info.getCcatCodes;

        superagentGetAsync(url)
          .then(
            (res) => {
              // console.log(`res.body.rslt.data=${displayJson(res.body.rslt.data)}`);
              const newCcatMenu = res.body.rslt.data[0].navigation;
              const newQrMenus = res.body.rslt.data[1];

              const newFlatMenu = [];
              flattenMenu(newCcatMenu, 0, newFlatMenu);

              // console.log(`newFlatMenu=${displayJson(newFlatMenu)}`);
              setFlatCcatMenu(newFlatMenu);

              // console.log(`newQrMenus=${stringSelectNLines(displayJson(newQrMenus), 100)}`);
              const newFlatQrMenu = [];
              flattenQrMenu(newQrMenus, newFlatQrMenu, 'main', 0);
              setQrMenus(newQrMenus);
              // console.log(`newFlatQrMenu=${stringSelectNLines(displayJson(newFlatQrMenu), 30)}`);
              setFlatQrMenus(newFlatQrMenu);
            },
          )
          .catch((err) => {
            console.log(`townAssetsStore err=${err}`);
          });
      }
    },
    [
      authStatus, authInfo, flatCcatMenu, flatCcatMenuPending, language,
      languagePending, superagentGetAsync,
    ],
  );

  useEffect(
    () => {
    // Load languages when logged in, or on public pages once auth resolves
      const authReady = (authStatus === 'loggedIn') || (isPublic && (authStatus !== 'init'));
      if (authReady && (!languagesPending)) {
      // console.log('Load available languages');
        // Use void to make it clear we do not want to wait this async function:
        void loadLanguages();
      }
    },
    [authStatus, isPublic, languagesPending, loadLanguages],
  );

  useEffect(
    () => {
    // Load language when logged in, or on public pages once auth resolves
      const authReady = (authStatus === 'loggedIn') || (isPublic && (authStatus !== 'init'));
      if (authReady && defaultLanguage && (!languagePending)) {
      // console.log(`Load default language "${defaultLanguage}"`);
        // Use void to make it clear we do not want to wait this async function:
        void loadLanguage(defaultLanguage);
      }
    },
    [authStatus, isPublic, defaultLanguage, languagePending, loadLanguage],
  );

  //
  // Fetch all customer images (except case discussion media)
  //
  const fetchImagesList = useCallback(
    async (sortBy) => {
      const url = config.town.back.urlPrefix
                + config.town.back.media.image.list;

      const payload = {};
      if (sortBy && sortBy.length) payload.sortBy = sortBy;

      try {
        const res = await superagentPostAsync(
          url,
          [['Content-Type', 'application/json']],
          payload,
        );

        if (isMountedRef.current) {
          console.log(`images=${displayJson(res.body.rslt.data)}`);
          setImagesList(res.body.rslt.data);
        }
      } catch (err) {
        console.log(`fetchImagesList err=${err}`);
      }
    },
    [isMountedRef, superagentPostAsync],
  );

  //
  // Upload a customer images, return its id
  //
  const uploadImage = useCallback(
    async (file, fileInfo, compressIfNeeded = true) => {
      let error;
      let res;
      try {
        // console.log(`Upload icon "${label}" ${url}`);

        const info = {
          type: fileInfo.type,
        };
        if (fileInfo.label) info.label = fileInfo.label;
        if (fileInfo.notes) info.notes = fileInfo.notes;

        if (file instanceof File) {
          let compressedFile;
          if (fileInfo.type === 'comm') {
            compressedFile = await compressCommImage(file);
          } else if (compressIfNeeded) {
            compressedFile = await compressImageIfNeeded(file);
          } else {
            compressedFile = file;
          }

          res = await cstmrPostBlob({
            piid: fileInfo.piid,
            rlid: authInfo?.rlid,
            urlPath: config.town.back.media.image.upload,
            headers: ['Accept', 'application/json'],
            info,
            infoFieldName: 'info',
            blob: compressedFile,
            blobFieldName: 'image',
          });
        } else {
          logError(
            `uploadImage - wrong file type ${typeof file}`,
            'TownAssetsProvider.uploadImage.wrongFileType',
            {
              fileType: typeof file,
              infoType: fileInfo?.type,
            },
          );

          res = {};
        }

        // console.log(`uploadImage res.body=${displayJson(res.body)}`);

        if (res.status !== 200) {
          error = new Error(`uploadImage response status=${res.status}`);
        }
        return res.body.rslt.data[0].id;
      } catch (err) {
        error = err;
      }

      logError(
        `uploadImage Error:${error}`,
        'TownAssetsProvider.uploadImage.error',
        {
          err: error?.message ?? String(error),
          ...(error?.status && { status: error.status }),
          ...(res?.status && { resStatus: res.status }),
          fileInfoType: fileInfo?.type,
        },
      );
      return undefined;
    },
    [cstmrPostBlob, authInfo?.rlid],
  );

  //
  // Update a customer image
  //
  const editImage = useCallback(
    async (file, fileInfo) => {
      let error;
      let res;
      try {
        if (fileInfo.id === undefined) throw new Error(`editImage missing id fileInfo=${JSON.stringify(fileInfo)}`);

        // console.log(`Upload icon "${label}" ${url}`);

        const info = {
          id: fileInfo.id,
        };
        if (fileInfo.type) info.type = fileInfo.type;
        if (fileInfo.label) info.label = fileInfo.label;
        if (fileInfo.notes) info.notes = fileInfo.notes;

        if (file) {
          if (file instanceof File) {
            const compressedFile = (fileInfo.type === 'comm')
              ? await compressCommImage(file)
              : await compressImageIfNeeded(file);

            res = await cstmrPostBlob({
              urlPath: config.town.back.media.image.edit,
              headers: ['Accept', 'application/json'],
              info,
              infoFieldName: 'info',
              blob: compressedFile,
              blobFieldName: 'image',
            });
          } else {
            logError(
              `uploadImage - wrong file type ${typeof file}`,
              'TownAssetsProvider.editImage.wrongFileType',
              {
                fileType: typeof file,
                infoId: fileInfo?.id,
              },
            );
            res = {};
          }
        } else {
          res = await cstmrPost({
            urlPath: config.town.back.media.image.edit,
            headers: ['Accept', 'application/json'],
            payload: { info: JSON.stringify(info) },
          });
        }

        if (res.status !== 200) {
          error = new Error(`editImage response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (error) {
        logError(
          `editImage Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'TownAssetsProvider.editImage.error',
          {
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
            ...(res?.status && { resStatus: res.status }),
            id: fileInfo?.id,
          },
        );
      }
    },
    [cstmrPost, cstmrPostBlob],
  );

  const getCachedSecuredImg = useCallback(
    (key) => {
      const item = securedImgCache.current.get(key);

      if (item) {
        // To maintain that the most recently accessed item is the last in the Map,
        // delete the key and re-set it, so it moves to the end.
        securedImgCache.current.delete(key);
        securedImgCache.current.set(key, item);
      }

      return item;
    },
    [],
  );

  const addCachedSecuredImg = useCallback(
    (key, item) => {
      // Before adding the new item, check if we need to evict the oldest item
      if (securedImgCache.current.size >= maxCacheItems) {
        // Keys().next().value returns the first item's key
        const oldestKey = securedImgCache.current.keys().next().value;

        const oldestUrl = securedImgCache.current.get(oldestKey);
        if (oldestUrl) URL.revokeObjectURL(oldestUrl);

        securedImgCache.current.delete(oldestKey);
      }

      securedImgCache.current.set(key, item);
    },
    [],
  );

  const startNextSecuredImg = useCallback(
    () => {
      if (securedImgRunning.current >= maxSecuredImgInFlight) return;

      const next = securedImgQueue.current.shift();
      if (!next) return;

      securedImgRunning.current += 1;
      next();
    },
    [],
  );

  const fetchSecuredImg = useCallback(
    async (urlPath, piid) => {
      try {
        const getArgs = {
          urlPath,
          returnBlob: true,
        };
        if (piid) {
          getArgs.piid = piid;
          getArgs.rlid = authInfo?.rlid;
        }

        const response = await cstmrGet(getArgs);

        // The www server answers an unknown path with index.html, so a misbuilt url
        // arrives as a page rather than as an error
        const contentType = response?.headers?.['content-type'] ?? '';

        if (contentType.includes('text/html')) {
          logError(
            `getSecuredImg served a page instead of an image, urlPath=${urlPath}`,
            'TownAssetsProvider.getSecuredImg.notAnImage',
            { urlPath, piid, contentType },
          );
        } else if (response?.body) {
          const imgUrl = URL.createObjectURL(response.body);

          addCachedSecuredImg(securedImgKey(urlPath, piid), imgUrl);

          return imgUrl;
        }
      } catch (err) {
        logError(
          `getSecuredImg urlPath=${urlPath} err=${err}`,
          'TownAssetsProvider.getSecuredImg.error',
          {
            urlPath,
            piid,
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
      }

      return null;
    },
    [cstmrGet, addCachedSecuredImg, authInfo?.rlid],
  );

  const getSecuredImg = useCallback(
    async (urlPath, piid = undefined) => {
      const key = securedImgKey(urlPath, piid);
      const cachedImg = getCachedSecuredImg(key);

      if (cachedImg) return cachedImg;

      const pending = securedImgPending.current.get(key);

      if (pending) return pending;

      let settle;
      const request = new Promise((resolve) => {
        settle = resolve;
      });

      securedImgPending.current.set(key, request);

      securedImgQueue.current.push(async () => {
        let imgUrl = null;

        try {
          imgUrl = await fetchSecuredImg(urlPath, piid);
        } finally {
          securedImgRunning.current -= 1;
          securedImgPending.current.delete(key);
          settle(imgUrl);
          startNextSecuredImg();
        }
      });

      startNextSecuredImg();

      return request;
    },
    [getCachedSecuredImg, fetchSecuredImg, startNextSecuredImg],
  );

  const flushSecuredImg = useCallback(
    (urlPath, piid = undefined) => {
      const key = securedImgKey(urlPath, piid);
      const cachedUrl = securedImgCache.current.get(key);

      if (cachedUrl) {
        URL.revokeObjectURL(cachedUrl);
        securedImgCache.current.delete(key);

        console.log(`flushSecuredImg ${cachedUrl}`);
      } else {
        console.log(`flushSecuredImg ${cachedUrl}, not cached yet`);
      }
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      assets: {
        ready,
        languages,
        language,
        loadLanguage,
        translate,
        flatCcatMenu,
        qrMenus,
        flatQrMenus,
        fetchImagesList,
        imagesList,
        uploadImage,
        editImage,
        getSecuredImg,
        flushSecuredImg,
      },
    }),
    [
      flatCcatMenu, languages, language, loadLanguage, ready, translate,
      qrMenus, flatQrMenus, fetchImagesList, imagesList, uploadImage,
      editImage, getSecuredImg, flushSecuredImg,
    ],
  );

  return (
    <TownAssetsContext.Provider value={contextValue}>
      {children}
    </TownAssetsContext.Provider>
  );
}

TownAssetsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultLanguage: PropTypes.string,
  public: PropTypes.bool,
};

export default TownAssetsProvider;
